import React, {useState} from "react";
import {Params, useLocation, useNavigate, useParams} from "react-router-dom";
import PageActions from "../../../../base/PageActions";
import WorldContent from "../../../../../model/WorldContent";
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";
import ContentBox from "../../../../../components/content-box/ContentBox";
import useBrowserNavigation from "../../../../../hooks/useBrowserNavigation";
import useFetchResult from "../../../../../hooks/UseFetchResult";
import _naming_service from "../../../../../services/NamingService";
import _http_service from "../../../../../services/HttpService";
import _device_service from "../../../../../services/DeviceService";
import _html_parser from "../../../../../services/HtmlService";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';

interface WorldSectionPageProps {
    section?: string,
    subsection?: string
}

interface WorldSectionPageParams extends Params {
    section: string,
    subsection?: string,
    topic?: string
}

const WorldSectionPage = (props: WorldSectionPageProps) => {

    const params = useParams<WorldSectionPageParams>();

    const getSection = (): string => {
        return (props.section ? props.section : params.section!);
    }

    const getSubsection = (): string => {
        return (props.subsection ? props.subsection : params.subsection!);
    }

    const [currentPath, setCurrentPath] = useState<string>(getSection()
        + (getSubsection().trim().length > 0 ? `/${getSubsection()}` : '') + (params.topic ? `/${params.topic}` : ''));

    const getDescription = (): string => {
        if (params.topic) {
            return `Learn about ${_naming_service.getSectionProperName(params.topic)} in the Casarok 
            ${_naming_service.getSectionProperName(getSection())} regarding Chase's world!`;
        } else if (getSubsection()) {
            return `Learn about ${_naming_service.getSectionProperName(getSubsection())} in the Casarok 
            ${_naming_service.getSectionProperName(getSection())} regarding Chase's world!`;
        } else {
            return `The Casarok ${_naming_service.getSectionProperName(getSection())} section, 
            detailing information about Chase's world!`;
        }
    }

    const getKeywords = (): string[] => {
        let keywords = ["casarok", "chase's world", _naming_service.getKeywordName(getSection())];
        if (getSubsection()) {
            keywords = keywords.concat(_naming_service.getKeywordName(getSubsection()))
        }
        if (params.topic) {
            keywords = keywords.concat(_naming_service.getKeywordName(params.topic));
        }

        return keywords;
    }

    const getTitleSubsection = (): string => {
        if (params.topic) {
            return ` (${_naming_service.getSectionProperName(params.topic)})`;
        } else if (getSubsection()) {
            return ` (${_naming_service.getSectionProperName(getSubsection())})`;
        } else {
            return '';
        }
    }

    const navigateTo = useNavigate();
    const location = useLocation();

    const [getResult, hasFetchedResult, setFetchedResult] = useFetchResult<WorldContent>(
        {pageLeft: [] as string[], pageRight: [] as string[]},
        () => _http_service.getWorldContentByPath(currentPath)
            .then(response => {
                if (!response) {
                    throw new Error('No response for world content!');
                }

                return response;
            }),
        [currentPath]);

    const page: PageActions = new PageActions({
        title: `Casarok: ${_naming_service.getSectionProperName(getSection())}${getTitleSubsection()} - Chase the Fox`,
        description: getDescription(),
        keywords: getKeywords(),
        imgSrc: (getResult() && getResult().cardImageSrc) ? getResult().cardImageSrc! : `world/${currentPath}/preview.png`,
        url: `chase/world/${currentPath}`
    });

    useBrowserNavigation((path) => {
        setCurrentPath(path.substring(path.lastIndexOf('world/') + 'world/'.length));
        setFetchedResult(false);
    });

    const navigateToPage = (): void => {
        let path = location.pathname;
        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }

        if (path.startsWith('world/') || path.startsWith('/world/')) {
            setCurrentPath(path.substring(path.lastIndexOf('world/') + 'world/'.length));
            setFetchedResult(false);
        } else {
            navigateTo(path, {replace: false});
        }

        _device_service.goToTop();
    }

    const switchVisible = (): void => {
        let initiallyVisibleTarget: HTMLElement = document.getElementById('visible-element')!;
        let initiallyHiddenTarget: HTMLElement = document.getElementById('hidden-element')!;

        if (initiallyVisibleTarget.className.includes('visible')) {
            initiallyVisibleTarget.classList.add('switch-hidden');
            initiallyVisibleTarget.classList.remove('switch-visible');
            initiallyHiddenTarget.classList.add('switch-visible');
            initiallyHiddenTarget.classList.remove('switch-hidden');
        } else {
            initiallyHiddenTarget.classList.add('switch-hidden');
            initiallyHiddenTarget.classList.remove('switch-visible');
            initiallyVisibleTarget.classList.add('switch-visible');
            initiallyVisibleTarget.classList.remove('switch-hidden');
        }
    }

    return page.generateMetaContent(
        <>
            <ContentBox>
                <Breadcrumbs path={`world/${currentPath}`} callback={navigateToPage}/>
                <div>
                    {hasFetchedResult && _html_parser.parse(getResult().pageLeft!.join('\n')
                            .concat(getResult().pageRight!.join('\n'))
                            .replace(new RegExp("class=\"character-info\"", 'g'), "class=\"world-info\"")
                            .replace(new RegExp("class=\"world-link glowing\"", 'g'), "class=\"world-link\"")
                            .replace(new RegExp("/world/pantheon", 'g'), "/world/religion/pantheon")
                            .replace(new RegExp("/world/mythology", 'g'), "/world/religion/mythology"),
                        () => navigateToPage(),
                        () => switchVisible()
                    )}
                </div>
            </ContentBox>
        </>
    );
}

export default WorldSectionPage;